import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { isNil, isNull } from 'lodash';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Collapse, Box, Typography, Grid, Button, Divider } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { grey } from '@mui/material/colors';
import { useNavigate } from 'react-router-dom';

import { sanitizeHtml, formatCurrency } from 'shared/functions';
import { TabsPanel, InfoTooltip } from 'ui';
import { MessageModal } from 'ui/Modals';
import { SummaryCardDocuments } from 'modules/common/SummaryCard';
import { addNotificationAction } from 'store/notifications/notificationsActions';

import { shopProductUnits } from '../../shared/constants/shopProductUnits';
import { shopViews } from '../../shared/constants/shopViews';
import { getProductsByIds, getProductsProperties } from '../../selectors/shopSelectors';
import { sendMessageToSales } from '../../services';
import {
  addShopProductsToBasketAction,
  startShopProductFreeSubscriptionAction,
} from '../../store/shopActions';
import { ShopProductFreeSubscriptionModal } from './ShopProductFreeSubscriptionModal';

const useStyle = makeStyles((theme) => ({
  stats: {
    textAlign: 'center',
  },
  statValue: {
    color: theme.palette.primary.main,
  },
  statLabel: {
    fontWeight: 'bold',
  },
  footer: {
    borderWidth: '5px 0 0 0',
    borderStyle: 'solid',
    borderColor: grey[300],
  },
  checkoutButton: {
    backgroundColor: '#ff8900',
    width: 160,
    height: 45,
    marginLeft: 15,
  },
  button: {
    height: 45,
  },
  price: {
    fontWeight: 500,
  },
  sidebar: {
    borderWidth: '0 0 0 3px',
    borderStyle: 'solid',
    borderColor: grey[300],
  },
  container: {
    marginTop: theme.spacing(4),
  },
  subtitle: {
    color: theme.palette.primary.main,
  },
  divider: {
    marginTop: 5,
    backgroundColor: theme.palette.text.primary,
  },
  detailsInfo: {
    fontSize: 16,
    fontWeight: 500,
    marginBottom: theme.spacing(2),
  },
  detailsInfoList: {
    textAlign: 'right',
  },
  description: {
    fontSize: 16,
  },
  tooltip: {
    position: 'relative',
    top: 2,
    marginRight: 5,
  },
  documents: {
    '& p': {
      fontWeight: 500,
      fontSize: 16,
    },
    '& button': {
      fontSize: 16,
      color: theme.palette.primary.main,
    },
  },
  subscriptionButton: {
    height: 45,
    marginLeft: 10,
  },
}));

const ShopProductDetails = ({ product, expanded, preview }) => {
  const { t } = useTranslation();
  const classes = useStyle();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { data: properties } = useSelector(getProductsProperties);
  const productIds = product.packageProducts ? product.packageProducts.map(({ id }) => id) : [];
  const products = useSelector(getProductsByIds(productIds));

  const productsById = products.reduce(
    (prodsById, prod) => ({ ...prodsById, [prod.id]: prod }),
    {},
  );

  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const toggleMessageModal = () => setMessageModalOpen(!messageModalOpen);

  const [isFreeSubscriptionModalOpen, setIsFreeSubscriptionModalOpen] = React.useState(false);

  const toggleFreeSubscriptionModal = () => {
    setIsFreeSubscriptionModalOpen((isOpen) => !isOpen);
  };

  const tabs = [{ id: product.id, label: t('Overview') }];

  const activeTab = tabs[0].label;

  const documents = product.documents
    ? product.documents.map(({ id, name }) => ({
        title: name.split('.')[0],
        path: `/shop/file-download/${id}/`,
        name,
      }))
    : [];

  const getPrice = () => {
    const pricing = (product.pricing ? product.pricing : []).find(({ active }) => active);

    return pricing ? pricing.price : null;
  };

  const getPriceText = () => {
    const price = getPrice();

    if (isNull(price)) {
      return t('On request');
    }

    return price === 0 ? t('Free of charge') : formatCurrency(price, 'EUR');
  };

  const getActivationFee = () => {
    const pricing = (product.pricing ? product.pricing : []).find(({ active }) => active);

    return pricing ? pricing.activationFee : null;
  };

  const getDescription = (id, index) => {
    if (index === 0) return sanitizeHtml(product.description);

    return productsById[id] ? sanitizeHtml(productsById[id].description) : null;
  };

  const getAmountLabel = (amount) => {
    const [productLine] = product.productLines;
    const productUnit = shopProductUnits[productLine];

    return amount && productUnit && `${amount} ${productUnit}`;
  };

  const getTrialPeriod = (period) => {
    if (!period) return null;

    const weeks = Math.floor(period / 7);
    const days = period % 7;

    const weeksLabel = t(`${weeks} week${weeks === 1 ? '' : 's'}`);
    const daysLabel = t(`${days} day${days === 1 ? '' : 's'}`);

    if (weeks && days) return `${weeksLabel} ${daysLabel}`;

    return weeks ? weeksLabel : daysLabel;
  };

  const sendMessage = (messageData) => {
    sendMessageToSales(messageData)
      .then(() => {
        dispatch(
          addNotificationAction({
            message: t('Message sent successfully'),
            variant: 'success',
          }),
        );
      })
      .catch(() => {
        dispatch(
          addNotificationAction({
            message: t('Failed to send a message'),
            variant: 'error',
          }),
        );
      });
  };

  const addToBasket = () => {
    dispatch(addShopProductsToBasketAction({ ids: [product.id] }));

    navigate({
      search: `?tab=${shopViews.basket}`,
    });
  };

  const activateFreeSubscription = () => {
    dispatch(startShopProductFreeSubscriptionAction({ id: product.id }));
  };

  const price = getPrice();
  const toolType = properties.tools.find(({ id }) => id === product.toolType);
  const amount = getAmountLabel(product.amount);
  const trialAmount = getAmountLabel(product.trialAmount);
  const trialPeriod = getTrialPeriod(product.trialPeriod);

  const purchasedButton = (
    <Button variant="outlined" color="primary" className={classes.subscriptionButton} disabled>
      {t('Subscribed')}
    </Button>
  );

  const addToBasketButton =
    price === 0 ? (
      <>
        <Button
          variant="outlined"
          color="primary"
          className={classes.subscriptionButton}
          onClick={toggleFreeSubscriptionModal}
          disabled={preview}
        >
          {t('Get started')}
        </Button>
        {!preview && (
          <ShopProductFreeSubscriptionModal
            open={isFreeSubscriptionModalOpen}
            onConfirm={activateFreeSubscription}
            onClose={toggleFreeSubscriptionModal}
            data={product}
          />
        )}
      </>
    ) : (
      <Button
        variant="contained"
        color="primary"
        className={classes.checkoutButton}
        disabled={preview}
        onClick={addToBasket}
      >
        {t('Add to basket')}
      </Button>
    );

  return (
    <Collapse in={expanded || preview} timeout="auto" unmountOnExit>
      <Grid container spacing={2} className={classes.container}>
        <Grid item xs={8}>
          <Box p={4} pt={0}>
            <TabsPanel active={activeTab}>
              {tabs.map(({ id, label }, index) => (
                <Box p={2} pt={4} label={label} value={label} key={id}>
                  <Box className={classes.description}>{getDescription(id, index)}</Box>
                </Box>
              ))}
            </TabsPanel>
          </Box>
        </Grid>
        <Grid item xs={4} className={classes.sidebar}>
          <Box px={4}>
            <Typography variant="h6" className={classes.subtitle}>
              {t('Product details')}
            </Typography>
            <Divider className={classes.divider} />
            <Box mt={2}>
              <Grid container justifyContent="space-between" className={classes.detailsInfo}>
                <Grid item>{t('Category:')}</Grid>
                <Grid item className={classes.detailsInfoList}>
                  {product.categories.map((category) => (
                    <Box key={category}>{category}</Box>
                  ))}
                </Grid>
              </Grid>
              <Grid container justifyContent="space-between" className={classes.detailsInfo}>
                <Grid item>
                  <InfoTooltip
                    title={t(
                      'The ongoing fee is charged regularly. The interval is defined by the contract term.',
                    )}
                    iconClassName={classes.tooltip}
                  />
                  {t('Ongoing fee:')}
                </Grid>
                <Grid item>{price === 0 ? t('Free of charge') : formatCurrency(price, 'EUR')}</Grid>
              </Grid>
              <Grid container justifyContent="space-between" className={classes.detailsInfo}>
                <Grid item>
                  <InfoTooltip
                    title={t(
                      'The activation fee is a one-time fee charged at the beginning of the contract term. The fee covers Scope’s costs of setting up the account and subscriptions.',
                    )}
                    iconClassName={classes.tooltip}
                  />
                  {t('Activation fee:')}
                </Grid>
                <Grid item>
                  {getActivationFee() === 0
                    ? t('Free of charge')
                    : formatCurrency(getActivationFee(), 'EUR')}
                </Grid>
              </Grid>
              <Grid container justifyContent="space-between" className={classes.detailsInfo}>
                <Grid item>
                  <InfoTooltip
                    title={t('The contract term defines the duration of the subscription.')}
                    iconClassName={classes.tooltip}
                  />
                  {t('Contract term:')}
                </Grid>
                <Grid item>{product.validity}</Grid>
              </Grid>
              <Grid container justifyContent="space-between" className={classes.detailsInfo}>
                <Grid item>
                  <InfoTooltip
                    title={t(
                      'The notice period describes the interval during which subscriptions can be cancelled latest. It always refers to the actual end of the subscription period.',
                    )}
                    iconClassName={classes.tooltip}
                  />
                  {t('Notice period:')}
                </Grid>
                <Grid item>{product.cancelPeriod}</Grid>
              </Grid>
              <Grid container justifyContent="space-between" className={classes.detailsInfo}>
                <Grid item>{t('Source:')}</Grid>
                <Grid item className={classes.detailsInfoList}>
                  {product.sources.map((source) => (
                    <Box key={source}>{source}</Box>
                  ))}
                </Grid>
              </Grid>
              {toolType && (
                <Grid container justifyContent="space-between" className={classes.detailsInfo}>
                  <Grid item>{t('Tool type:')}</Grid>
                  <Grid item className={classes.detailsInfoList}>
                    <Box>{toolType.name}</Box>
                  </Grid>
                </Grid>
              )}
              {!isNil(amount) && (
                <Grid container justifyContent="space-between" className={classes.detailsInfo}>
                  <Grid item>{t('Subscribed amount:')}</Grid>
                  <Grid item className={classes.detailsInfoList}>
                    <Box>{amount}</Box>
                  </Grid>
                </Grid>
              )}
              {!isNil(trialAmount) && (
                <Grid container justifyContent="space-between" className={classes.detailsInfo}>
                  <Grid item>{t('Free trial amount:')}</Grid>
                  <Grid item className={classes.detailsInfoList}>
                    <Box>{trialAmount}</Box>
                  </Grid>
                </Grid>
              )}
              {!isNil(trialPeriod) && (
                <Grid container justifyContent="space-between" className={classes.detailsInfo}>
                  <Grid item>{t('Free trial term:')}</Grid>
                  <Grid item className={classes.detailsInfoList}>
                    <Box>{trialPeriod}</Box>
                  </Grid>
                </Grid>
              )}
            </Box>
          </Box>
          <Box px={4}>
            <Typography variant="h6" className={classes.subtitle}>
              {t('Documents')}
            </Typography>
            <Divider className={classes.divider} />
            <Box mt={2}>
              <SummaryCardDocuments
                documentTypes={documents}
                className={classes.documents}
                disabled={preview}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>
      <Box p={4} mt={4} className={classes.footer} hidden={preview}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography variant="h5" className={classes.price}>
              {getPriceText()}
            </Typography>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              className={classes.button}
              onClick={toggleMessageModal}
              disabled={preview}
            >
              {t('Contact Sales')}
            </Button>
            {!isNil(price) && !product.purchased && addToBasketButton}
            {product.purchased && purchasedButton}
          </Grid>
        </Grid>
      </Box>
      <MessageModal
        title={t('Contact Sales')}
        open={messageModalOpen}
        onClose={toggleMessageModal}
        onSendMessage={sendMessage}
      />
    </Collapse>
  );
};

ShopProductDetails.defaultProps = {
  preview: false,
};

ShopProductDetails.propTypes = {
  expanded: PropTypes.bool.isRequired,
  product: PropTypes.object.isRequired,
  preview: PropTypes.bool,
};

export { ShopProductDetails };
